<template>
  <v-dialog
    :value="visiblePopup"
    fullscreen
    @keydown.esc="onCloseClickButton"
    @click:outside="onCloseClickButton"
  >
    <v-card class="popup-wrapper">
      <div class="details-container">
        <div class="cloud">
          <div class="close" @click="onCloseClickButton">X {{ $t('newProject.esc') }}</div>
          <div class="avatar">
            <v-avatar>
              <img v-if="details.avatarURL" class="avatar" :src="(details.avatarURL)" />
              <v-icon v-else class="initials">{{ initials }}</v-icon>
            </v-avatar>
          </div>
          <div class="name-country">
            <div class="name">{{ details.firstName }} {{ details.lastName }}</div>
            <div class="country">{{ companyName }} </div>
          </div>
          <div class="finance-block">
            <div class="hours-rate finance-cell">
              <div class="finance-value">${{ profile.hourRate || 0 }}</div>
              <div class="finance-title">
                {{ $t('newProject.worker.hourly.rate') }}
              </div>
            </div>
            <div class="jobs finance-cell">
              <div class="finance-value">
                {{ profile.experience || 0 }}
              </div>
              <div class="finance-title">
                {{ $t('newProject.worker.experience') }}
              </div>
            </div>
          </div>
        </div>
        <div class="roof">
          <div class="services skill-cell">
            <div class="skill-title">
              {{ $t('services') }}
            </div>
            <div class="skill-value">
              <div v-for="service in profile.services" :key="service.name">
                <span :title="service.name">{{ service.name }}</span>
              </div>
            </div>
          </div>
          <div class="certificates skill-cell">
            <div class="skill-title">
              {{ $t('worker.certificates') }}
            </div>
            <div class="skill-value">
              <span v-for="(certificate, index) in certificates" :key="certificate.code">
                <a v-if="certificate.fileUrl" :href="certificate.fileUrl" :title="$t(`${certificate.code}`)" target="_blank">{{
                    $t(`${certificate.code}`)
                }}</a>
                <span v-else>{{ $t(`${certificate.code}`) }}</span>
                <span v-if="index !== profile.certificates.length - 1"> </span>
              </span>
            </div>
          </div>
        </div>
        <div class="cellar">
          <div class="summary">
            <div class="summary-title">{{ $t('summary') }}</div>
            <div class="summary-value">{{ profile.summary }}</div>
          </div>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import disableOuterScrollOnPopupVisible from '@/mixins/disableOuterScrollOnPopupVisible';

export default {
  name: 'WorkerDetailsPopup',
  mixins: [disableOuterScrollOnPopupVisible],
  props: {
    worker: Object,
    visiblePopup: {
      type: Boolean,
      required: true,
    },
  },
  computed: {
    initials() {
      return `${this.worker?.firstName?.[0].toUpperCase()}${this.worker?.lastName?.[0].toUpperCase()}`;
    },
    companyName() {
      if (this.worker?.isFreelancer) {
        return this.$t('freelancer');
      }
      return this.worker?.company?.name;
    },
    details() {
      return this.worker || {};
    },
    profile() {
      return this.details.workerProfile || {};
    },
    certificates() {
      if (this.worker?.workerProfile?.certificates) {
        return this.worker.workerProfile.certificates.map(certificate => ({ ...certificate, ...certificate.certificate }));
      }
      return this.worker?.certificates;
    },
  },
  methods: {
    onCloseClickButton() {
      this.$emit('closed', true);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/design-system';

.details-container {
  min-height: rem(450);
  max-height: rem(800);
  overflow-y: auto;
  width: rem(704);
  border-radius: $radius-md;
  background-color: var(--bg-primary);
  box-shadow: 0 0 11px 0 rgba(12, 18, 62, 0.13);

  top: rem(60);
  position: relative;
  margin: auto;

  .cloud {
    height: rem(164);
    border-radius: $radius-md $radius-md 0 0;
    background-color: var(--bg-secondary);
    padding: rem(32) rem(40);
    box-shadow: 0 0.5px 0.5px 0 rgba(12, 18, 62, 0.13);

    .avatar {
      height: rem(101);
      width: rem(101);
      display: inline-block;
      margin-left: rem(16);
      position: relative;

      img.avatar {
        width: rem(101);
        height: rem(101);
        display: inline-block;
        margin-left: 0px;
        bottom: 0;
      }

      ::v-deep .v-avatar {
        min-width: rem(101) !important;
        width: rem(101) !important;
        min-height: rem(101) !important;
        height: rem(101) !important;
      }

      .initials {
        background-color: rgba(14, 31, 51, 0.15);
        border-radius: rem(50);
        color: var(--text-secondary);
        display: block;
        height: rem(101);
        width: rem(101);
        font-size: rem(50);
        font-family: $font-family-semibold;
        padding-top: rem(27);
      }
    }

    .name-country {
      margin-left: rem(35);
      height: rem(34);
      width: rem(103);
      display: inline-block;
      position: relative;
      text-transform: capitalize;
      top: rem(16);

      .name {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;

        color: var(--text-primary);
        font-size: rem(20);
        font-weight: bold;
        letter-spacing: 0;
        line-height: rem(24);
        width: rem(200);
      }

      .country {
        width: rem(210);
        overflow: hidden;
        text-overflow: ellipsis;

        color: var(--text-tertiary);
        font-size: rem(14);
        letter-spacing: 0;
        line-height: rem(16);
      }
    }

    .finance-block {
      display: inline-block;
      position: relative;
      top: rem(30);
      float: right;
      margin-right: rem(15);

      .finance-cell {
        margin-left: rem(25);
        display: inline-block;
      }

      .finance-value {
        color: var(--text-primary);
        font-size: rem(20);
        font-weight: bold;
        letter-spacing: 0;
        line-height: rem(24);
      }

      .finance-title {
        color: var(--text-tertiary);
        font-size: rem(14);
        letter-spacing: 0;
        line-height: rem(16);
      }
    }

    .pending-changes-alert {
      display: inline-block;
      position: absolute;
      left: rem(10);
      top: rem(10);
      font-size: rem(12);
      font-weight: 500;
      letter-spacing: rem(1.07);
      line-height: rem(16);
      color: var(--brand-primary);
    }

    .close {
      cursor: pointer;
      display: inline-block;
      position: absolute;
      right: rem(10);
      top: rem(10);
      font-size: rem(12);
      font-weight: 500;
      letter-spacing: rem(1.07);
      line-height: rem(16);
      color: var(--brand-primary);
    }
  }

  .roof {
    .skill-cell {
      display: inline-grid;
      margin-left: rem(60);
      margin-top: rem(24);
      width: 40%;

      .skill-title {
        text-transform: uppercase;
        height: rem(10);
        color: var(--text-tertiary);
        font-size: rem(10);
        letter-spacing: rem(0.8);
        line-height: rem(10);
      }

      .skill-value {
        margin-top: rem(11);
        font-size: rem(14);
        letter-spacing: 0;

        overflow: hidden;
        text-overflow: ellipsis;
      }

      .skill-value > span::after {
        content: ', ';
      }

      .skill-value > span:last-child::after {
        content: '';
      }
    }

    .services .skill-value {
      white-space: nowrap;
    }
  }

  .cellar {
    .summary {
      margin-left: rem(60);
      margin-top: rem(32);
      margin-bottom: rem(20);

      .summary-title {
        text-transform: uppercase;
        height: rem(10);
        color: var(--text-tertiary);
        font-size: rem(10);
        letter-spacing: rem(0.8);
        line-height: rem(10);
      }
      .summary-value {
        margin-top: rem(11);
        width: rem(424);
      }
    }
  }
}
</style>
